import React, { useMemo, useEffect } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import LoadingOverlay from 'components/LoadingOverlay';
import PhoneInput from 'products/nomad_insurance/components/PhoneInput';
import TextInput from 'products/nomad_insurance/components/TextInput';
import Select from 'products/nomad_insurance/components/Select';
import { setPlanholderInfo, setValidity } from './state';
import validation from './validation';
import { StyledTitle, InputWrapper, StyledNextRow, BeneficiaryText } from './styled';
import StepView from '../../components/StepView';
import { useStepNavigationContext } from '../../context';
import { SubTitle, InfoText, NextButton } from '../../styled';
import { trackHubSpotEvent } from '../../utils/hubspot/hubspot-custom-events';

const BasicInfo = () => {
  const dispatch = useDispatch();
  const { changeStep, jumpToFirstStep, isRenewal } = useStepNavigationContext();
  const { planholder, isLoading } = useSelector(
    (state) => state.nomadInsurance.application
  );
  const { activePaidContracts } = useSelector((state) => state.nomadInsurance.contract);
  const { beneficiary } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.basicInfo
  );
  const { isValid } = useSelector(
    (state) => state.nomadInsurance.purchaseInsurance.coverage
  );

  if (!isValid) {
    jumpToFirstStep();
  }

  const hasContract = activePaidContracts && activePaidContracts.length > 0;

  const previousContract = useMemo(() => {
    if (isRenewal && hasContract && activePaidContracts?.length > 0) {
      const contract = activePaidContracts[0];
      return {
        beneficiary: contract.beneficiary,
        gender: contract.planHolder?.gender,
        phoneNumber: contract.planHolder?.phoneNumber,
        phoneCountryCode: contract.planHolder?.phoneCountryCode
      };
    }
    return {};
  }, [isRenewal, activePaidContracts]);

  const navigateToNextStep = (formValues) => {
    dispatch(setPlanholderInfo(formValues));

    void trackHubSpotEvent('basicInfo', planholder.email);

    changeStep(1);
  };

  const formik = useFormik({
    initialValues: useMemo(
      () => ({
        phoneNumber: planholder?.phoneNumber || previousContract.phoneNumber || '',
        phoneCountryCode:
          planholder?.phoneCountryCode || previousContract.phoneCountryCode || '',
        gender: planholder?.gender?.toLowerCase() || previousContract.gender || '',
        beneficiary: beneficiary || previousContract.beneficiary || ''
      }),
      [planholder, beneficiary, previousContract]
    ),
    enableReinitialize: true,
    // TODO: Save properties in the backend
    validationSchema: validation,
    onSubmit: navigateToNextStep
  });

  useEffect(() => {
    dispatch(setValidity(formik.isValid));
  }, [formik.isValid, dispatch]);

  return (
    <StepView>
      <LoadingOverlay isLoading={isLoading} variant="light" />
      <StyledTitle>Basic information</StyledTitle>
      <SubTitle>Your phone number</SubTitle>
      <InputWrapper>
        <PhoneInput
          formik={formik}
          variant="gray"
          label="Phone number"
          name="phoneNumber"
        />
      </InputWrapper>

      <SubTitle>Your sex</SubTitle>
      <InfoText>The sex listed on your passport</InfoText>
      <InputWrapper>
        <Select
          showIndicator
          formik={formik}
          variant="gray"
          name="gender"
          label="Sex"
          options={[
            {
              value: 'female',
              label: 'Female'
            },
            {
              value: 'male',
              label: 'Male'
            }
          ]}
        />
      </InputWrapper>
      <SubTitle>Beneficiary</SubTitle>
      <BeneficiaryText>
        In case anything happens, name a beneficiary for your policy.
      </BeneficiaryText>
      <InputWrapper>
        <TextInput
          formik={formik}
          variant="gray"
          name="beneficiary"
          label="Beneficiary full name"
          comment="Who is your beneficiary in case something happens to you?"
          tooltip="Who will receive a benefit in the case of your accidental death? This is typically a spouse, child, parent etc."
        />
      </InputWrapper>
      <StyledNextRow>
        <NextButton
          color="confetti"
          onClick={formik.submitForm}
          disabled={!formik.isValid || isLoading}
        >
          Next
        </NextButton>
        <InfoText>Next up, add your trip destinations</InfoText>
      </StyledNextRow>
    </StepView>
  );
};

export default BasicInfo;
